import type { Produktdaten, Produktkreis, Produktpunkt,
              Anlagendaten } from '../api';

/**
 * Die Schemabausteine.
 *
 * --- Warum zehn Bausteine und kein Seitengenerator -----------------------------
 *
 * Alles Uebrige der Produktseite ist generisch: Die Endpunkte liefern
 * Feldgruppen, Kreise und Betriebspunkte, und die Seite zeigt, was kommt. Eine
 * neue Bauteilart braucht dort keine Zeile Code, nur Zeilen im Feldkatalog.
 *
 * Nur das Schema ist es nicht. Eine Kaeltemaschine hat zwei Kreise gegenueber,
 * ein Heizkoerper einen Vor- und Ruecklauf von links, ein Verteiler einen Zulauf
 * und acht Abgaenge. Das sind Bauformen, keine Datensaetze.
 *
 * Zehn davon deckt das Gewerk ab - und dieselben zehn tragen fuer Sanitaer und
 * Sprinkler: Ein Sprinklerkopf ist ein Verbraucher, eine Zentrale ein Verteiler,
 * ein Trinkwassererwaermer ein Zweikreiser. Deshalb heissen sie fachlich und
 * nicht 'schema1'.
 *
 * Die Auswahl kommt aus heizung_bauteilarten.Schemaart. Eine neue Bauteilart ist
 * damit eine Zeile in der Datenbank; nur eine wirklich neue Bauform ist ein
 * elfter Baustein.
 *
 * --- Warum SVG und keine Bilder -----------------------------------------------
 *
 * Ein Schema laesst sich PRUEFEN. Wer eine verwechselte Vor- und Ruecklaufseite
 * sieht, sieht sie sofort - an einem warmen Pfeil, wo ein kalter stehen muesste.
 * Eine Fotografie zeigt nur, wie das Geraet aussieht.
 */

const F = {
  warm: '#e8845f',
  kalt: '#5fb8e0',
  linie: 'rgba(128,150,170,0.28)',
  fuellung: 'rgba(128,150,170,0.10)',
  schwach: 'rgba(154,184,208,0.85)',
  sehrSchwach: 'rgba(154,184,208,0.55)',
  gruen: '#7ec98f',
  luft: 'rgba(154,184,208,0.5)',
};

const zahl = (n: number | null | undefined, stellen = 0) =>
  n === null || n === undefined
    ? '–'
    : n.toLocaleString('de-DE', {
        minimumFractionDigits: stellen, maximumFractionDigits: stellen });

const kW = (w: number | null | undefined) =>
  w === null || w === undefined ? '' : `${zahl(w / 1000, 0)} kW`;

/** Die Farbe nach der Temperatur, nicht nach dem Kreisnamen. */
const farbe = (t: number | null | undefined) =>
  t === null || t === undefined ? F.schwach : t > 20 ? F.warm : F.kalt;

const punktVon = (k: Produktkreis | undefined, modus: string) =>
  k?.betriebspunkte.find((p) => p.modus === modus)
  ?? k?.betriebspunkte.find((p) => p.istAuslegung)
  ?? k?.betriebspunkte[0];

// ═══ Die Auswahl ═════════════════════════════════════════════════════════════

export function Schema({ d, modus }: { d: Produktdaten; modus: string }) {
  const art = d.schemaart ?? 'armatur';

  if (art === 'keines') return null;

  const inhalt =
      art === 'zweikreis'    ? <Zweikreis d={d} modus={modus} />
    : art === 'verbraucher'  ? <Verbraucher d={d} modus={modus} />
    : art === 'fancoil'      ? <Fancoil d={d} modus={modus} />
    : art === 'rueckkuehler' ? <Rueckkuehler d={d} modus={modus} />
    : art === 'pumpe'        ? <Pumpe d={d} modus={modus} />
    : art === 'verteiler'    ? <Verteiler d={d} modus={modus} />
    : art === 'speicher'     ? <Speicher d={d} modus={modus} />
    : art === 'grenze'       ? <Grenze d={d} />
    :                          <Armatur d={d} />;

  return (
    <svg viewBox="0 0 760 250" width="100%"
         style={{ maxWidth: 760, display: 'block', margin: '0 auto' }}
         role="img" aria-label={`Schema ${art}`}>
      {inhalt}
    </svg>
  );
}

// ═══ Bausteine ═══════════════════════════════════════════════════════════════

/**
 * Zwei Hydraulikkreise gegenueber, dazwischen der Prozess.
 *
 * Verdampfer oben, Verfluessiger unten - so wie ein Kaelteprozess gezeichnet
 * wird: kalt oben zum Verbraucher, warm unten zur Rueckkuehlung. Bei einem
 * Waermetauscher primaer oben, sekundaer unten.
 */
function Zweikreis({ d, modus }: { d: Produktdaten; modus: string }) {
  const oben = d.kreise.find((k) => k.kreisart === 'verdampfer')
            ?? d.kreise.find((k) => k.kreisart === 'primaer');
  const unten = d.kreise.find((k) => k.kreisart === 'verfluessiger')
             ?? d.kreise.find((k) => k.kreisart === 'sekundaer');

  return (
    <>
      <Gehaeuse titel={d.modell ?? d.bauteilart} />
      {d.bauteilart === 'kaeltemaschine' && <Verdichter d={d} />}
      {d.bauteilart === 'waermetauscher' && <Gegenstrom />}
      {oben && <Anschlusspaar y={92} k={oben} p={punktVon(oben, modus)}
                              titel="Verdampfer" nachOben />}
      {unten && <Anschlusspaar y={158} k={unten} p={punktVon(unten, modus)}
                               titel="Verflüssiger" nachOben={false} />}
    </>
  );
}

/**
 * Vor- und Ruecklauf von links, Leistung an den Raum.
 *
 * Der Heizkoerper als Rippen, die Heiz-/Kuehldecke als Flaeche - beides so
 * gezeichnet, dass die Bauform erkennbar ist, ohne beschriftet zu sein.
 */
function Verbraucher({ d, modus }: { d: Produktdaten; modus: string }) {
  const kreise = d.kreise.length ? d.kreise : [];
  const k = kreise[0];
  const p = punktVon(k, modus);
  const decke = d.bauteilart === 'heizkuehldecke';

  return (
    <>
      {decke ? (
        <>
          <rect x={300} y={80} width={330} height={22} rx={3}
                fill={F.fuellung} stroke={F.linie} strokeWidth={1.4} />
          {Array.from({ length: 11 }, (_, i) => (
            <line key={i} x1={312 + i * 30} y1={102} x2={312 + i * 30} y2={112}
                  stroke={F.linie} strokeWidth={1} />
          ))}
          <text x={465} y={130} textAnchor="middle" fontSize={10}
                fill={F.sehrSchwach}>Heiz- und Kühlfläche</text>
        </>
      ) : (
        <>
          <rect x={330} y={72} width={280} height={78} rx={3}
                fill={F.fuellung} stroke={F.linie} strokeWidth={1.4} />
          {Array.from({ length: 13 }, (_, i) => (
            <line key={i} x1={342 + i * 21} y1={76} x2={342 + i * 21} y2={146}
                  stroke={F.linie} strokeWidth={1.2} />
          ))}
        </>
      )}

      <text x={465} y={54} textAnchor="middle" fontSize={12} fill={F.schwach}>
        {d.modell ?? d.typ ?? d.bauteilart}
        {p?.leistungW ? ` · ${kW(p.leistungW)}` : ''}
      </text>

      {/* Vor- und Ruecklauf von links, uebereinander - so liegen sie am Bauteil */}
      {k && (
        <>
          <Zulauf y={decke ? 86 : 90} t={p?.eintrittC ?? null}
                  text="Vorlauf" bis={decke ? 300 : 330} />
          <Zulauf y={decke ? 96 : 132} t={p?.austrittC ?? null}
                  text="Rücklauf" bis={decke ? 300 : 330} umgekehrt />
          <Mediumzeile k={k} p={p} y={decke ? 160 : 178} />
        </>
      )}
      {!k && <KeinKreis />}
    </>
  );
}

/** Verbraucher mit Luftseite - Wasser waagerecht, Luft senkrecht. */
function Fancoil({ d, modus }: { d: Produktdaten; modus: string }) {
  const k = d.kreise[0];
  const p = punktVon(k, modus);

  return (
    <>
      <rect x={310} y={80} width={300} height={90} rx={3}
            fill={F.fuellung} stroke={F.linie} strokeWidth={1.4} />

      {/* Das Register */}
      {Array.from({ length: 8 }, (_, i) => (
        <line key={i} x1={340 + i * 18} y1={92} x2={340 + i * 18} y2={158}
              stroke={F.linie} strokeWidth={1.2} />
      ))}

      {/* Der Ventilator */}
      <circle cx={545} cy={125} r={22} fill="none" stroke={F.schwach}
              strokeWidth={1.4} />
      <path d="M 545 108 Q 556 120 545 125 Q 534 130 545 142 Q 556 130 545 125"
            fill="none" stroke={F.schwach} strokeWidth={1.3} />

      {/* Die Luft: von unten ein, nach oben aus */}
      <line x1={400} y1={210} x2={400} y2={172} stroke={F.luft}
            strokeWidth={2} strokeDasharray="4 3" />
      <path d="M 395 180 L 400 170 L 405 180 Z" fill={F.luft} />
      <text x={408} y={202} fontSize={9} fill={F.sehrSchwach}>Zuluft</text>

      <line x1={500} y1={78} x2={500} y2={40} stroke={F.luft}
            strokeWidth={2} strokeDasharray="4 3" />
      <path d="M 495 50 L 500 40 L 505 50 Z" fill={F.luft} />
      <text x={508} y={48} fontSize={9} fill={F.sehrSchwach}>Ausblas</text>

      <text x={460} y={64} textAnchor="middle" fontSize={12} fill={F.schwach}>
        {d.modell ?? d.typ ?? 'Ventilatorkonvektor'}
        {p?.leistungW ? ` · ${kW(p.leistungW)}` : ''}
      </text>

      {k ? (
        <>
          <Zulauf y={98} t={p?.eintrittC ?? null} text="Vorlauf" bis={310} />
          <Zulauf y={152} t={p?.austrittC ?? null} text="Rücklauf" bis={310}
                  umgekehrt />
          <Mediumzeile k={k} p={p} y={192} />
        </>
      ) : <KeinKreis />}
    </>
  );
}

/** Wasserkreis mit Ventilatoren gegen Aussenluft. */
function Rueckkuehler({ d, modus }: { d: Produktdaten; modus: string }) {
  const k = d.kreise[0];
  const p = punktVon(k, modus);

  return (
    <>
      {/* Die Waermetauscherflaeche als Dach */}
      <path d="M 300 150 L 380 96 L 540 96 L 620 150 Z"
            fill={F.fuellung} stroke={F.linie} strokeWidth={1.4} />

      {/* Die Ventilatoren oben */}
      {[420, 460, 500].map((x) => (
        <g key={x}>
          <circle cx={x} cy={80} r={13} fill="none" stroke={F.schwach}
                  strokeWidth={1.3} />
          <path d={`M ${x} 69 Q ${x + 8} 76 ${x} 80 Q ${x - 8} 84 ${x} 91`}
                fill="none" stroke={F.schwach} strokeWidth={1.2} />
        </g>
      ))}
      <line x1={460} y1={58} x2={460} y2={34} stroke={F.luft} strokeWidth={2}
            strokeDasharray="4 3" />
      <path d="M 455 44 L 460 34 L 465 44 Z" fill={F.luft} />
      <text x={470} y={42} fontSize={9} fill={F.sehrSchwach}>Fortluft</text>

      {/* Die Aussenluft - bei einem Trockenrueckkuehler die eigentliche
          Randbedingung. Sie unterscheidet die Betriebsarten. */}
      {p?.hinweis?.includes('12 Grad C') ? (
        <text x={300} y={44} fontSize={9} fill={F.luft}>Aussenluft 12 °C</text>
      ) : null}

      <text x={460} y={172} textAnchor="middle" fontSize={12} fill={F.schwach}>
        {d.modell ?? 'Rückkühler'}
        {p?.leistungW ? ` · ${kW(p.leistungW)}` : ''}
      </text>

      {k ? (
        <>
          <Zulauf y={118} t={p?.eintrittC ?? null} text="Eintritt" bis={318} />
          <Zulauf y={140} t={p?.austrittC ?? null} text="Austritt" bis={310}
                  umgekehrt />
          <Mediumzeile k={k} p={p} y={196} />
          <Punktstreifen k={k} modus={modus} y={222} />
        </>
      ) : <KeinKreis />}
    </>
  );
}

/**
 * Alle Betriebspunkte eines Kreises nebeneinander.
 *
 * --- Warum nebeneinander und nicht nur der gewaehlte -------------------------
 *
 * Bei einem Rueckkuehler ist der VERGLEICH die Aussage. Kondensatorbetrieb bei
 * 32 Grad C Aussenluft, freie Kuehlung bei 12 Grad C und Eisspeicherladung sind
 * drei verschiedene Anlagen im selben Geraet - und der Unterschied entscheidet,
 * ob die Rueckkuehlung reicht.
 *
 * Der Modusschalter bleibt und hebt den gewaehlten Punkt hervor.
 *
 * --- Und warum die leeren mitkommen ------------------------------------------
 *
 * Zwei der drei Punkte sind leer: Der Güntner-Auslegungssatz enthaelt genau
 * einen und verlangt fuer abweichende Lufttemperatur eine neue Selektion.
 *
 * Ein leerer Punkt wird gezeigt, nicht weggelassen. Weggelassen wuesste niemand,
 * dass er fehlt - und genau das ist die Information, die zur Anfrage an den
 * Hersteller fuehrt.
 */
function Punktstreifen({ k, modus, y }: {
  k: Produktkreis; modus: string; y: number;
}) {
  const punkte = k.betriebspunkte;

  if (punkte.length < 2) return null;

  // Die Breite je Spalte aus der Zahl der Punkte. Bei drei Punkten 200,
  // bei vier 150 - die Zeichnung ist 620 breit.
  const breite = Math.min(200, Math.floor(600 / punkte.length));
  const start = 300 - (punkte.length * breite) / 2 + 160;

  return (
    <g>
      <line x1={160} y1={y - 12} x2={620} y2={y - 12}
            stroke={F.sehrSchwach} strokeWidth={0.8} />
      <text x={160} y={y} fontSize={9} fill={F.sehrSchwach}>Betriebspunkte</text>

      {punkte.map((q, i) => {
        const x = start + i * breite;
        const gewaehlt = q.modus === modus;
        const leer = q.leistungW === null;

        return (
          <g key={q.id}>
            {gewaehlt ? (
              <rect x={x - 6} y={y - 10} width={breite - 4} height={62}
                    fill={F.fuellung} stroke={F.linie} strokeWidth={0.8}
                    rx={3} />
            ) : null}

            <text x={x} y={y} fontSize={9}
                  fill={gewaehlt ? F.linie : F.sehrSchwach}>
              {q.modusDe ?? q.modus}
              {q.istAuslegung ? ' ▲' : ''}
            </text>

            {leer ? (
              <>
                <text x={x} y={y + 16} fontSize={10} fill={F.luft}>
                  offen
                </text>
                <text x={x} y={y + 30} fontSize={8} fill={F.sehrSchwach}>
                  Herstellerselektion
                </text>
                <text x={x} y={y + 42} fontSize={8} fill={F.sehrSchwach}>
                  erforderlich
                </text>
              </>
            ) : (
              <>
                <text x={x} y={y + 16} fontSize={11}
                      fill={gewaehlt ? F.linie : F.schwach}>
                  {kW(q.leistungW)}
                </text>
                <text x={x} y={y + 30} fontSize={8} fill={F.sehrSchwach}>
                  {q.eintrittC !== null && q.austrittC !== null
                    ? `${q.eintrittC}/${q.austrittC} °C`
                    : '—'}
                </text>
                <text x={x} y={y + 42} fontSize={8} fill={F.sehrSchwach}>
                  {q.druckverlustPa !== null
                    ? `${(q.druckverlustPa / 1000).toFixed(0)} kPa`
                    : ''}
                  {q.massenstromKgH !== null
                    ? ` · ${Math.round(q.massenstromKgH / 1000)} t/h`
                    : ''}
                </text>
              </>
            )}
          </g>
        );
      })}

      {/* Das Dreieck markiert den Auslegungspunkt - den, nach dem
          dimensioniert wird. */}
      <text x={160} y={y + 42} fontSize={8} fill={F.sehrSchwach}>
        ▲ Auslegung
      </text>
    </g>
  );
}

/** Durchgang mit Laufrad. Die Foerderhoehe ist die Aussage. */
function Pumpe({ d, modus }: { d: Produktdaten; modus: string }) {
  const k = d.kreise[0];
  const p = punktVon(k, modus);

  return (
    <>
      <line x1={40} y1={125} x2={340} y2={125} stroke={F.kalt} strokeWidth={2.4} />
      <line x1={420} y1={125} x2={720} y2={125} stroke={F.kalt} strokeWidth={2.4} />
      <path d="M 322 120 L 336 125 L 322 130 Z" fill={F.kalt} />
      <path d="M 706 120 L 720 125 L 706 130 Z" fill={F.kalt} />

      {/* Das Kreiselzeichen: Kreis mit Dreieck, wie im Schema ueblich */}
      <circle cx={380} cy={125} r={38} fill={F.fuellung} stroke={F.schwach}
              strokeWidth={1.5} />
      <path d="M 364 105 L 364 145 L 400 125 Z" fill="none" stroke={F.schwach}
            strokeWidth={1.5} />

      <text x={380} y={62} textAnchor="middle" fontSize={12} fill={F.schwach}>
        {d.modell ?? 'Pumpe'}
      </text>
      <text x={380} y={196} textAnchor="middle" fontSize={11} fill={F.schwach}>
        {p?.volumenstromM3H ? `${zahl(p.volumenstromM3H, 1)} m³/h` : ''}
        {p?.druckverlustPa
          ? ` · ${zahl(p.druckverlustPa / 100, 1)} kPa`
          : ' · Förderhöhe fehlt'}
      </text>
      {k && <Mediumzeile k={k} p={p} y={214} />}
    </>
  );
}

/** Ein Zulauf, mehrere Abgaenge. */
function Verteiler({ d, modus }: { d: Produktdaten; modus: string }) {
  const k = d.kreise[0];
  const p = punktVon(k, modus);
  const n = 6;

  return (
    <>
      {/* Der Balken */}
      <rect x={200} y={112} width={460} height={26} rx={4}
            fill={F.fuellung} stroke={F.linie} strokeWidth={1.5} />

      <line x1={40} y1={125} x2={200} y2={125} stroke={farbe(p?.eintrittC)}
            strokeWidth={3} />
      <path d="M 184 119 L 198 125 L 184 131 Z" fill={farbe(p?.eintrittC)} />
      <text x={44} y={116} fontSize={12} fill={farbe(p?.eintrittC)}
            fontWeight={600}>
        {p?.eintrittC === null || p?.eintrittC === undefined
          ? '–' : `${zahl(p.eintrittC)} °C`}
      </text>

      {/* Die Abgaenge nach unten, mit Absperrung */}
      {Array.from({ length: n }, (_, i) => {
        const x = 240 + i * 74;
        return (
          <g key={i}>
            <line x1={x} y1={138} x2={x} y2={196} stroke={F.linie}
                  strokeWidth={2} />
            <path d={`M ${x - 7} 158 L ${x + 7} 168 L ${x + 7} 158
                      L ${x - 7} 168 Z`}
                  fill="none" stroke={F.schwach} strokeWidth={1.2} />
            <text x={x} y={212} textAnchor="middle" fontSize={9}
                  fill={F.sehrSchwach}>{i + 1}</text>
          </g>
        );
      })}

      <text x={430} y={92} textAnchor="middle" fontSize={12} fill={F.schwach}>
        {d.modell ?? 'Verteiler'}
        {p?.volumenstromM3H ? ` · ${zahl(p.volumenstromM3H, 1)} m³/h` : ''}
      </text>
      <text x={430} y={234} textAnchor="middle" fontSize={9} fill={F.sehrSchwach}>
        Zahl der Abgänge schematisch — die tatsächliche ergibt sich aus dem Netz
      </text>
      {k && <Mediumzeile k={k} p={p} y={106} />}
    </>
  );
}

/** Behaelter mit Schichtung. */
function Speicher({ d, modus }: { d: Produktdaten; modus: string }) {
  const k = d.kreise[0];
  const p = punktVon(k, modus);
  const ausdehnung = d.bauteilart === 'ausdehnung';

  return (
    <>
      <rect x={340} y={50} width={140} height={150} rx={ausdehnung ? 68 : 10}
            fill={F.fuellung} stroke={F.linie} strokeWidth={1.6} />

      {!ausdehnung && (
        // Die Schichtung: warm oben, kalt unten. Beim Eisspeicher ist genau
        // das der Zweck.
        <>
          {[76, 96, 116, 136, 156, 176].map((y, i) => (
            <line key={y} x1={352} y1={y} x2={468} y2={y}
                  stroke={i < 3 ? F.warm : F.kalt} strokeWidth={1}
                  opacity={0.45} />
          ))}
        </>
      )}
      {ausdehnung && (
        <line x1={340} y1={125} x2={480} y2={125} stroke={F.linie}
              strokeWidth={1.4} strokeDasharray="5 4" />
      )}

      <text x={410} y={36} textAnchor="middle" fontSize={12} fill={F.schwach}>
        {d.modell ?? d.bauteilart}
      </text>

      {k ? (
        <>
          <Zulauf y={80} t={p?.eintrittC ?? null} text="Eintritt" bis={340} />
          <Zulauf y={170} t={p?.austrittC ?? null} text="Austritt" bis={340}
                  umgekehrt />
          <Mediumzeile k={k} p={p} y={222} />
        </>
      ) : (
        <>
          <line x1={100} y1={125} x2={340} y2={125} stroke={F.linie}
                strokeWidth={2} />
          <KeinKreis />
        </>
      )}
    </>
  );
}

/**
 * Die Systemgrenze.
 *
 * Der Anschlusspunkt ist kein Bauteil, sondern eine Grenze - zu einem anderen
 * Modell, einem anderen Gewerk oder einem Geraet ohne Anschluesse in der
 * Familie. Genau das zeigt der Baustein: die Leitung endet an einer Linie, und
 * dahinter steht, was dort anschliesst.
 */
function Grenze({ d }: { d: Produktdaten }) {
  return (
    <>
      <line x1={60} y1={125} x2={370} y2={125} stroke={F.kalt} strokeWidth={2.4} />
      <path d="M 352 119 L 366 125 L 352 131 Z" fill={F.kalt} />

      <line x1={380} y1={62} x2={380} y2={188} stroke={F.schwach}
            strokeWidth={1.6} strokeDasharray="7 5" />
      <text x={380} y={52} textAnchor="middle" fontSize={10} fill={F.sehrSchwach}>
        Systemgrenze
      </text>

      <rect x={430} y={96} width={230} height={58} rx={4} fill="none"
            stroke={F.linie} strokeWidth={1.3} strokeDasharray="5 4" />
      <text x={545} y={122} textAnchor="middle" fontSize={12} fill={F.schwach}>
        {d.typ ?? 'anschließendes Gerät'}
      </text>
      <text x={545} y={140} textAnchor="middle" fontSize={9} fill={F.sehrSchwach}>
        liegt in einem anderen Modell oder Gewerk
      </text>
    </>
  );
}

/**
 * Durchgangsbauteil mit Ventilzeichen.
 *
 * Das Zeichen kommt aus der Bauteilart: Absperrung, Regelung, Rueckschlag und
 * Messtechnik sehen im Schema verschieden aus, und ein Planer liest sie am
 * Zeichen, nicht an der Beschriftung.
 */
function Armatur({ d }: { d: Produktdaten }) {
  const a = d.bauteilart;
  const x = 380, y = 125;

  return (
    <>
      <line x1={60} y1={y} x2={x - 22} y2={y} stroke={F.kalt} strokeWidth={2.4} />
      <line x1={x + 22} y1={y} x2={700} y2={y} stroke={F.kalt} strokeWidth={2.4} />

      {/* Das Doppeldreieck ist die Grundform jeder Armatur */}
      <path d={`M ${x - 20} ${y - 15} L ${x} ${y} L ${x - 20} ${y + 15} Z
                M ${x + 20} ${y - 15} L ${x} ${y} L ${x + 20} ${y + 15} Z`}
            fill="none" stroke={F.schwach} strokeWidth={1.6} />

      {a === 'absperrarmatur' && (
        // Handrad
        <>
          <line x1={x} y1={y - 15} x2={x} y2={y - 34} stroke={F.schwach}
                strokeWidth={1.5} />
          <line x1={x - 13} y1={y - 34} x2={x + 13} y2={y - 34}
                stroke={F.schwach} strokeWidth={2} />
        </>
      )}
      {a === 'regelarmatur' && (
        // Stellantrieb als Kaestchen
        <>
          <line x1={x} y1={y - 15} x2={x} y2={y - 30} stroke={F.schwach}
                strokeWidth={1.5} />
          <rect x={x - 14} y={y - 46} width={28} height={16} rx={2}
                fill={F.fuellung} stroke={F.schwach} strokeWidth={1.4} />
          <text x={x} y={y - 34} textAnchor="middle" fontSize={9}
                fill={F.schwach}>M</text>
        </>
      )}
      {a === 'rueckschlag' && (
        // Die Klappe als Strich im Durchgang
        <line x1={x} y1={y - 15} x2={x + 12} y2={y + 12} stroke={F.gruen}
              strokeWidth={2} />
      )}
      {a === 'messtechnik' && (
        <>
          <line x1={x} y1={y - 15} x2={x} y2={y - 32} stroke={F.schwach}
                strokeWidth={1.4} />
          <circle cx={x} cy={y - 42} r={11} fill={F.fuellung} stroke={F.schwach}
                  strokeWidth={1.4} />
          <line x1={x} y1={y - 42} x2={x + 6} y2={y - 48} stroke={F.schwach}
                strokeWidth={1.3} />
        </>
      )}
      {a === 'kompensator' && (
        // Die Balgform statt des Doppeldreiecks andeuten
        <path d={`M ${x - 20} ${y - 12} q 5 12 0 24 M ${x - 10} ${y - 12}
                  q 5 12 0 24 M ${x} ${y - 12} q 5 12 0 24
                  M ${x + 10} ${y - 12} q 5 12 0 24`}
              fill="none" stroke={F.schwach} strokeWidth={1.2} />
      )}

      <text x={x} y={196} textAnchor="middle" fontSize={12} fill={F.schwach}>
        {d.modell ?? d.typ ?? d.bauteilbezeichnung ?? a}
      </text>
      {d.kreis && (
        <text x={x} y={214} textAnchor="middle" fontSize={9}
              fill={F.sehrSchwach}>{d.kreis}</text>
      )}
    </>
  );
}

// ═══ Gemeinsame Teile ════════════════════════════════════════════════════════

function Gehaeuse({ titel }: { titel: string }) {
  return (
    <>
      <rect x={250} y={72} width={260} height={106} rx={4}
            fill={F.fuellung} stroke={F.linie} strokeWidth={1.4} />
      <text x={380} y={62} textAnchor="middle" fontSize={12} fill={F.schwach}>
        {titel}
      </text>
    </>
  );
}

/** So viele Verdichtersymbole, wie das Feld verdichter_anzahl nennt. */
function Verdichter({ d }: { d: Produktdaten }) {
  const feld = d.gruppen.flatMap((g) => g.felder)
    .find((f) => f.feldschluessel === 'verdichter_anzahl');

  const n = Math.min(Math.max(Math.round(feld?.zahlwert ?? 0), 0), 6);
  if (!n) return null;

  const start = 380 - (n * 26) / 2 + 13;

  return (
    <g>
      {Array.from({ length: n }, (_, i) => (
        <g key={i}>
          <circle cx={start + i * 26} cy={125} r={9} fill="none"
                  stroke={F.schwach} strokeWidth={1.3} />
          <path d={`M ${start + i * 26 - 4} 129 L ${start + i * 26 + 4} 121`}
                stroke={F.schwach} strokeWidth={1.3} />
        </g>
      ))}
      <text x={380} y={150} textAnchor="middle" fontSize={9} fill={F.sehrSchwach}>
        {n} Verdichter
      </text>
    </g>
  );
}

/** Die Gegenstromandeutung eines Waermetauschers. */
function Gegenstrom() {
  return (
    <g>
      {[108, 118, 128, 138].map((y, i) => (
        <line key={y} x1={276} y1={y} x2={484} y2={y}
              stroke={i % 2 ? F.kalt : F.warm} strokeWidth={1.4}
              opacity={0.55} />
      ))}
    </g>
  );
}

/** Ein Kreis als Anschlusspaar links und rechts am Gehaeuse. */
function Anschlusspaar({ y, k, p, titel, nachOben }: {
  y: number; k: Produktkreis; p?: Produktpunkt; titel: string;
  nachOben: boolean;
}) {
  const fe = farbe(p?.eintrittC);
  const fa = farbe(p?.austrittC);

  return (
    <g>
      <line x1={40} y1={y} x2={250} y2={y} stroke={fe} strokeWidth={2.4} />
      <path d={`M 232 ${y - 5} L 246 ${y} L 232 ${y + 5} Z`} fill={fe} />
      <text x={44} y={y - 9} fontSize={12} fill={fe} fontWeight={600}>
        {p?.eintrittC === null || p?.eintrittC === undefined
          ? '–' : `${zahl(p.eintrittC)} °C`}
      </text>
      <text x={44} y={y + 17} fontSize={9} fill={F.sehrSchwach}>Eintritt</text>

      <line x1={510} y1={y} x2={720} y2={y} stroke={fa} strokeWidth={2.4} />
      <path d={`M 706 ${y - 5} L 720 ${y} L 706 ${y + 5} Z`} fill={fa} />
      <text x={716} y={y - 9} fontSize={12} fill={fa} fontWeight={600}
            textAnchor="end">
        {p?.austrittC === null || p?.austrittC === undefined
          ? '–' : `${zahl(p.austrittC)} °C`}
      </text>
      <text x={716} y={y + 17} fontSize={9} fill={F.sehrSchwach}
            textAnchor="end">Austritt</text>

      <text x={380} y={nachOben ? y - 8 : y + 22} textAnchor="middle"
            fontSize={11} fill={F.schwach}>
        {titel}
        {p?.leistungW ? ` · ${kW(p.leistungW)}` : ''}
        {p?.volumenstromM3H ? ` · ${zahl(p.volumenstromM3H, 1)} m³/h` : ''}
      </text>
      <text x={380} y={nachOben ? y + 12 : y + 36} textAnchor="middle"
            fontSize={9} fill={F.sehrSchwach}>
        {k.medium}
        {k.glykolProzent ? ` ${zahl(k.glykolProzent)} %` : ''}
        {k.anschluss ? ` · ${k.anschluss}` : ''}
      </text>
    </g>
  );
}

/** Eine Zuleitung von links. */
function Zulauf({ y, t, text, bis, umgekehrt }: {
  y: number; t: number | null; text: string; bis: number; umgekehrt?: boolean;
}) {
  const c = farbe(t);

  return (
    <g>
      <line x1={60} y1={y} x2={bis} y2={y} stroke={c} strokeWidth={2.4} />
      {umgekehrt
        ? <path d={`M 78 ${y - 5} L 64 ${y} L 78 ${y + 5} Z`} fill={c} />
        : <path d={`M ${bis - 18} ${y - 5} L ${bis - 4} ${y}
                    L ${bis - 18} ${y + 5} Z`} fill={c} />}
      <text x={64} y={y - 8} fontSize={11} fill={c} fontWeight={600}>
        {t === null ? '–' : `${zahl(t)} °C`}
      </text>
      <text x={140} y={y - 8} fontSize={9} fill={F.sehrSchwach}>{text}</text>
    </g>
  );
}

function Mediumzeile({ k, p, y }: {
  k: Produktkreis; p?: Produktpunkt; y: number;
}) {
  return (
    <text x={380} y={y} textAnchor="middle" fontSize={9} fill={F.sehrSchwach}>
      {k.medium}
      {k.glykolProzent ? ` ${zahl(k.glykolProzent)} %` : ''}
      {p?.massenstromKgH ? ` · ${zahl(p.massenstromKgH)} kg/h` : ''}
      {k.anschluss ? ` · ${k.anschluss}` : ''}
    </text>
  );
}

/**
 * Der Hinweis, dass noch kein Kreis gepflegt ist.
 *
 * Nicht leer lassen: Ein Schema ohne Anschluesse sieht aus wie ein Fehler der
 * Anzeige. Der Satz sagt, dass es einer der Daten ist - und welcher.
 */
function KeinKreis() {
  return (
    <text x={380} y={222} textAnchor="middle" fontSize={10} fill={F.sehrSchwach}>
      Kein Hydraulikkreis gepflegt — ohne ihn keine Betriebspunkte und keine
      Auslegung
    </text>
  );
}

// ═══ Der Geräteaufbau eines Lüftungsgeräts ═══════════════════════════════════

/**
 * Die Zeichen je Funktionseinheit.
 *
 * Die Zuordnung ist ausdrücklich und vollständig; was fehlt, wird 'leer' und
 * bleibt sichtbar unbestimmt. Ein geratenes Symbol wäre schlimmer als ein
 * leeres Rechteck — es behauptete etwas.
 *
 * Die Schlüssel sind plant_units.Art, wie der Import sie setzt.
 */
const EINHEIT: Record<string, { zeichen: string; farbe: string; text: string }> = {
  RAHMEN: { zeichen: 'rahmen', farbe: F.sehrSchwach, text: 'Anschlussrahmen' },
  KLAPPE: { zeichen: 'klappe', farbe: F.schwach, text: 'Klappe' },
  ABSTAND: { zeichen: 'leer', farbe: F.sehrSchwach, text: 'Abstand' },
  FILTER: { zeichen: 'filter', farbe: F.kalt, text: 'Filter' },
  WRG: { zeichen: 'kreuz', farbe: F.gruen, text: 'WRG' },
  TROPF: { zeichen: 'tropf', farbe: F.kalt, text: 'Tropfenabscheider' },
  VENT: { zeichen: 'rad', farbe: F.warm, text: 'Ventilator' },
  SCHALL: { zeichen: 'kulissen', farbe: F.schwach, text: 'Schalldämpfer' },
  CHANGE: { zeichen: 'register', farbe: '#a17fc4', text: 'Change-Over' },
  ERH: { zeichen: 'register', farbe: F.warm, text: 'Erhitzer' },
  KUEHL: { zeichen: 'register', farbe: F.kalt, text: 'Kühler' },
  WETTER: { zeichen: 'wetter', farbe: F.schwach, text: 'Wetterschutz' },
  COIL: { zeichen: 'register', farbe: '#a17fc4', text: 'Register' },
};

/** Ein Zahlwert aus dem DataJson einer Einheit, n-tes Vorkommen. */
function ausDaten(json: string | null | undefined, label: string, nr = 1) {
  if (!json) return null;
  try {
    const d = JSON.parse(json) as Record<string, unknown>;
    // Der Import legt die Eigenschaften als Liste ab, nicht als Objekt mit
    // Namen — mehrere tragen denselben Namen, und ein Objekt hätte sie
    // überschrieben.
    const liste = Array.isArray(d) ? d : (d.properties as unknown[]) ?? [];
    let treffer = 0;
    for (const p of liste as Record<string, any>[]) {
      if (p?.label !== label) continue;
      treffer += 1;
      if (treffer === nr) return p?.measurement?.value ?? null;
    }
    return null;
  } catch {
    return null;
  }
}

/** Das Symbol einer Einheit. Reines SVG, ohne Schrift. */
function Zeichen({ art, x, y, b, h, c }: {
  art: string; x: number; y: number; b: number; h: number; c: string;
}) {
  const mx = x + b / 2;
  const my = y + h / 2;
  const t = [] as JSX.Element[];

  if (art === 'filter') {
    const n = Math.max(3, Math.round(b / 7));
    const p = Array.from({ length: n + 1 }, (_, i) =>
      `${x + (b * i) / n},${i % 2 ? y + 6 : y + h - 6}`).join(' ');
    t.push(<polyline key="f" points={p} fill="none" stroke={c} strokeWidth={1.2} />);
  } else if (art === 'rad') {
    const r = Math.min(b, h) / 2 - 5;
    t.push(<circle key="c" cx={mx} cy={my} r={r} fill="none" stroke={c}
                   strokeWidth={1.6} />);
    // Zwei Flügel als Bogen: Ein Freirad hat wenige, kein Sternmuster.
    t.push(<path key="a" d={`M ${mx} ${my} q ${r * 0.8} ${-r * 0.5} ${r * 0.9} ${r * 0.2}`}
                 fill="none" stroke={c} strokeWidth={1.6} />);
    t.push(<path key="b" d={`M ${mx} ${my} q ${-r * 0.8} ${r * 0.5} ${-r * 0.9} ${-r * 0.2}`}
                 fill="none" stroke={c} strokeWidth={1.6} />);
  } else if (art === 'register') {
    // Rohrschleifen — so zeichnet man ein Register im Schema.
    for (let i = 0; i < 3; i += 1) {
      const yy = y + (h * (i + 1)) / 4;
      t.push(<line key={`l${i}`} x1={x + 4} y1={yy} x2={x + b - 4} y2={yy}
                   stroke={c} strokeWidth={1.3} />);
    }
    for (let i = 0; i < 2; i += 1) {
      const y1 = y + (h * (i + 1)) / 4;
      const y2 = y + (h * (i + 2)) / 4;
      const seite = i % 2 === 0 ? x + b - 4 : x + 4;
      const r = Math.abs(y2 - y1) / 2;
      t.push(<path key={`b${i}`}
                   d={`M ${seite} ${y1} A ${r} ${r} 0 0 ${i % 2 === 0 ? 1 : 0} ${seite} ${y2}`}
                   fill="none" stroke={c} strokeWidth={1.3} />);
    }
  } else if (art === 'kreuz') {
    t.push(<line key="1" x1={x + 5} y1={y + 5} x2={x + b - 5} y2={y + h - 5}
                 stroke={c} strokeWidth={1.4} />);
    t.push(<line key="2" x1={x + b - 5} y1={y + 5} x2={x + 5} y2={y + h - 5}
                 stroke={c} strokeWidth={1.4} />);
  } else if (art === 'kulissen') {
    const n = Math.max(2, Math.round(b / 9));
    for (let i = 0; i < n; i += 1) {
      const xx = x + 5 + ((b - 10) * i) / Math.max(1, n - 1);
      t.push(<line key={i} x1={xx} y1={y + 5} x2={xx} y2={y + h - 5}
                   stroke={c} strokeWidth={2.4} strokeOpacity={0.55} />);
    }
  } else if (art === 'klappe') {
    for (let i = 0; i < 3; i += 1) {
      const yy = y + (h * (i + 1)) / 4;
      t.push(<line key={i} x1={x + 5} y1={yy + 5} x2={x + b - 5} y2={yy - 5}
                   stroke={c} strokeWidth={1.6} />);
    }
  } else if (art === 'tropf') {
    const n = Math.max(2, Math.round(b / 8));
    for (let i = 0; i < n; i += 1) {
      const xx = x + 5 + ((b - 10) * i) / Math.max(1, n - 1);
      t.push(<path key={i}
                   d={`M ${xx} ${y + 6} q 4 ${h / 3} 0 ${h * 0.66} q -4 ${-h / 3} 0 ${-h * 0.66}`}
                   fill="none" stroke={c} strokeWidth={1.1} />);
    }
  } else if (art === 'rahmen') {
    t.push(<line key="r" x1={mx} y1={y + 4} x2={mx} y2={y + h - 4}
                 stroke={c} strokeWidth={1.2} strokeDasharray="3 3" />);
  } else if (art === 'wetter') {
    for (let i = 0; i < 4; i += 1) {
      const yy = y + (h * (i + 1)) / 5;
      t.push(<line key={i} x1={x + 4} y1={yy} x2={x + b - 4} y2={yy - 4}
                   stroke={c} strokeWidth={1.3} />);
    }
  }

  return (
    <g>
      <rect x={x} y={y} width={b} height={h} rx={2} fill={c} fillOpacity={0.16}
            stroke={c} strokeWidth={1.4} />
      {t}
    </g>
  );
}

/**
 * Der Geräteaufbau: die Funktionseinheiten je Luftstrom.
 *
 * Die Reihenfolge ist die Durchströmungsrichtung und wird NICHT sortiert — sie
 * ist eine fachliche Angabe.
 *
 * Die Breite je Einheit folgt der Baulänge. Ein Gerät der Flachbaureihe ist
 * 4.284 mm lang; ohne Verhältnis sähen ein 100-mm-Anschlussrahmen und ein
 * 1.683-mm-Wärmetauscher gleich aus.
 */
export function Geraeteaufbau({ d }: { d: Anlagendaten }) {
  const stroeme = d.stroeme ?? [];
  if (stroeme.length === 0) return null;

  // Maße. Der linke Rand ist größer: Die gedrehten Beschriftungen ragen nach
  // links, und ohne den Platz stand dort 'chlussrahmen' statt 'Anschlussrahmen'.
  const RL = 74;
  const RR = 24;
  const KOPF = 26;
  const BAND = 74;
  const ZONE = 76;
  const LUECKE = 16;
  const FUSS = 56;
  const MINB = 44;
  const JE_MM = 0.1;

  const bahnen = stroeme.map((s) => ({
    strom: s,
    einheiten: (s.einheiten ?? []).map((u) => {
      const e = EINHEIT[u.art] ?? { zeichen: 'leer', farbe: F.schwach, text: u.art };
      const laenge = ausDaten(u.daten, 'Komponentenlänge')
                  ?? ausDaten(u.daten, 'Kulissenlänge')
                  ?? ausDaten(u.daten, 'Paketbreite');
      return {
        u,
        ...e,
        breite: Math.max(MINB, (Number(laenge) || 300) * JE_MM),
      };
    }),
  }));

  const inhalt = Math.max(...bahnen.map((b) =>
    b.einheiten.reduce((n, u) => n + u.breite + 3, 0)), 400);
  const breite = RL + Math.max(560, inhalt) + 56 + RR;
  const hoehe = KOPF + bahnen.length * (BAND + ZONE + LUECKE) + FUSS;

  return (
    <svg viewBox={`0 0 ${Math.round(breite)} ${Math.round(hoehe)}`} width="100%"
         role="img" aria-label="Geräteaufbau">
      {bahnen.map((b, i) => {
        const y = KOPF + i * (BAND + ZONE + LUECKE);
        let x = RL;

        // Die Bilanz: Summe der Einheiten ohne Ventilator gegen den internen
        // Druckverlust, den der Ventilator meldet. Weichen sie ab, fehlt eine
        // Einheit im Datenblattauszug.
        const vent = b.einheiten.find((u) => u.u.art === 'VENT');
        const intern = vent ? ausDaten(vent.u.daten, 'Interner Druckverlust') : null;
        const summe = b.strom.summeOhneVentilator ?? 0;
        const stimmt = intern !== null && Math.abs(Number(intern) - summe) < 0.5;

        return (
          <g key={b.strom.luftstrom}>
            <text x={RL} y={y - 10} fontSize={11} fontWeight={600}
                  fill={F.schwach}>
              {b.strom.luftstrom === 'ZU' ? 'Zuluft' : 'Abluft'}
              {' · '}{summe} Pa Einheiten
              {intern !== null && ` · Ventilator meldet ${intern} Pa`}
              {intern !== null && (stimmt ? ' · stimmig' : ' · ABWEICHUNG')}
            </text>

            {b.einheiten.map((u, k) => {
              const links = x;
              x += u.breite + 3;
              const mx = links + u.breite / 2;
              const ty = y + BAND + 8;
              return (
                <g key={u.u.id ?? k}>
                  <Zeichen art={u.zeichen} x={links} y={y} b={u.breite}
                           h={BAND} c={u.farbe} />
                  {u.u.druckverlustPa != null && (
                    <text x={mx} y={y + 11} fontSize={9} textAnchor="middle"
                          fill={F.sehrSchwach}>
                      {u.u.druckverlustPa} Pa
                    </text>
                  )}
                  {/* Gedreht und am Ende verankert: So läuft der Text vom
                      Symbol nach links unten weg und bleibt in seiner Zone. */}
                  <text x={mx} y={ty} fontSize={9.5} textAnchor="end"
                        fill={F.sehrSchwach}
                        transform={`rotate(-48 ${mx} ${ty})`}>
                    {u.text}
                  </text>
                </g>
              );
            })}

            {/* Der Luftpfeil — die Durchströmungsrichtung. */}
            <line x1={x + 8} y1={y + BAND / 2} x2={x + 44} y2={y + BAND / 2}
                  stroke={F.luft} strokeWidth={1.2} />
            <path d={`M ${x + 44} ${y + BAND / 2} l -6 -3.4 l 0 6.8 z`}
                  fill={F.luft} />
          </g>
        );
      })}
    </svg>
  );
}
